const R = require('ramda');
const moment = require('moment');
const { uniq } = require('../utils');
const { TRACKER_LIST } = require('../constants');

const groupBySubChannel = R.groupBy(R.prop('subChannel'));

const getListMinTime = R.reduce(R.minBy(getTime), new Date());

const getListMaxTime = R.reduce(R.maxBy(getTime), null);

function typeOfData(data) {
  if (data) {
    if (Array.isArray(data)) {
      return 'array';
    } else return typeof data;
  } else return false;
}

function sumObject(leftValue, rightValue) {
  if (leftValue == null) return rightValue;
  if (rightValue == null) return leftValue;
  const type = typeOfData(leftValue);
  if (type === 'array') {
    const arr = [];
    const nbElements = Math.max(leftValue.length, rightValue.length);
    for (let i = 0; i < nbElements; i++) {
      arr.push(sumObject(leftValue[i], rightValue[i]));
    }
    return arr;
  } else if (type === 'object') {
    const keys = uniq(Object.keys(leftValue).concat(Object.keys(rightValue)));
    return keys.reduce((acc, key) => {
      acc[key] = sumObject(leftValue[key], rightValue[key]);
      return acc;
    }, {});
  } else if (type === 'number') {
    return (leftValue || 0) + (rightValue || 0);
  } else return rightValue;
}

function sumSameActionValues(sameActionList) {
  if (sameActionList.length == 1) return sameActionList;
  const keysToSum = ['value', 'spend', 'clicks', 'spendOnActions', 'clicksOnActions', 'conversions'];
  const data = sameActionList.reduce((acc, action) => {
    const actionData = {
      ...R.pick(keysToSum, action),
    };
    return sumObject(acc, actionData);
  });
  Object.assign(data, R.pick(['name', 'type', 'key', 'actionValue'], sameActionList[0]));
  return data;
  // keys.map(key => {
  //   data[key] = R.compose(R.sum, R.map(R.prop(key)))(sameActionList);
  // });
  // return data;
}

function mergeActions(actions) {
  return mergeActions2(actions);
}
function mergeActions2(actions) {
  actions = actions.filter(Boolean);
  if (actions.length <= 1) return actions;
  const getNameAndType = action => {
    if (action.name === 'app_install') return action.name;
    return action.name + action.type;
  };
  // group by name and type
  let actionsByNameAndType = {};
  for (let action of actions) {
    let nameAndType = getNameAndType(action);
    if (!actionsByNameAndType[nameAndType]) actionsByNameAndType[nameAndType] = [];
    actionsByNameAndType[nameAndType].push(action);
  }
  let mergedActions = [];
  for (let actionGroup of Object.values(actionsByNameAndType)) {
    mergedActions.push(sumSameActionValues2(actionGroup));
  }
  return mergedActions;
}

function sumSameActionValues2(sameActionList) {
  let firstAction = sameActionList[0];
  let summedAction = {
    name: firstAction.name,
    type: firstAction.type,
    key: firstAction.key,
    actionValue: firstAction.actionValue,
    value: 0,
    spend: 0,
    clicks: 0,
    spendOnActions: 0,
    clicksOnActions: 0,
    conversions: 0,
  };
  for (let action of sameActionList) {
    summedAction.value += action.value || 0;
    summedAction.spend += action.spend || 0;
    summedAction.clicks += action.clicks || 0;
    summedAction.spendOnActions += action.spendOnActions || 0;
    summedAction.clicksOnActions += action.clicksOnActions || 0;
    summedAction.conversions += action.conversions || 0;
  }
  return summedAction;
}

function mergeMetrics(metrics) {
  if (metrics.length == 1) return metrics[0];
  return R.compose(R.reduce(sumObject, {}), R.filter(Boolean))(metrics);
}

function mergeConversionsByTracker(conversionsByTrackers) {
  return TRACKER_LIST.reduce((acc, tracker) => {
    const actions = conversionsByTrackers.filter(Boolean).flatMap(child => child[tracker]);
    acc[tracker] = mergeActions(actions);
    return acc;
  }, {});
}

function mergeConversionsByTrackerPerDate(conversionsByTrackerByDates) {
  return R.compose(
    R.map(conversionsByTrackers => {
      const { from, to } = conversionsByTrackers[0];
      const conversionsByTracker = mergeConversionsByTracker(conversionsByTrackers.map(R.prop('conversionsByTracker')));
      return {
        from,
        to,
        conversionsByTracker,
      };
    }),
    Object.values,
    R.groupBy(R.prop('from')),
  )(conversionsByTrackerByDates);
}

function mergeSubChannelConversions(conversionsByTrackerByDates) {
  return R.compose(
    R.map(conversionsByTrackers => {
      const { subChannel } = conversionsByTrackers[0];
      const conversionsByTracker = mergeConversionsByTracker(conversionsByTrackers.map(R.prop('conversionsByTracker')));
      return {
        subChannel,
        conversionsByTracker,
      };
    }),
    Object.values,
    R.groupBy(R.prop('subChannel')),
  )(conversionsByTrackerByDates);
}

function mergeSubChannelData(subChannelData) {
  return R.compose(
    R.map(subChannelData => {
      const { subChannel } = subChannelData[0];
      const metric = mergeMetrics(subChannelData.map(R.prop('metric')));
      const dailyBudget = subChannelData.reduce((acc, { dailyBudget }) => acc + (dailyBudget || 0), 0);
      const conversionsByTracker = mergeConversionsByTracker(subChannelData.map(R.prop('conversionsByTracker')));
      return {
        subChannel,
        dailyBudget,
        metric,
        conversionsByTracker,
        channelCampaignId: subChannelData.map(sc => sc.channelCampaignId),
      };
    }),
    Object.values,
    R.groupBy(R.prop('subChannel')),
  )(subChannelData);
}

function getTime(dateString) {
  if (!dateString) return 0;
  return new Date(dateString).getTime();
}

function getAdrielStatus(status) {
  if (['PAUSED', 'DELETED', 'UNDETERMINED', 'DISAPPROVED'].includes(status)) return 'deactivated';
  if (status === 'RUNNING') return 'running';
  if (status === 'IN_REVIEW') return 'in_review';
}

function transformGAMetric(metric) {
  const { users, newUsers, sessionDuration, bounceRate, pageviews, transactionRevenue } = metric;

  const newMetric = {
    visits: parseInt(users),
    newVisits: parseInt(newUsers),
    bouncedVisits: (parseFloat(bounceRate) / 100) * parseInt(users),
    totalVisitDuration: parseFloat(sessionDuration),
    totalPageViews: parseInt(pageviews),
    gaRevenue: parseFloat(transactionRevenue),
  };
  return newMetric;
}

function mergeReportArray(reports) {
  const actions = R.compose(mergeActions, R.flatten, R.map(R.prop('actions')))(reports);

  const metric = R.compose(mergeMetrics, R.map(R.prop('metric')))(reports);

  return {
    actions,
    metric,
  };
}

function mergeReportArrayByDate(reports) {
  return R.compose(
    R.map(reports => {
      const { from, to } = reports[0];
      return {
        from,
        to,
        ...mergeReportArray(reports),
      };
    }),
    Object.values,
    R.groupBy(R.prop('from')),
  )(reports);
}

function mergeReports(...reports) {
  return mergeReportArray(reports);
}

function getMetricFromList(list) {
  let metrics = [];
  for (let el of list) {
    let metric = el.metric;
    if (!metric || metric.length == 0) continue;
    metrics.push(metric);
  }
  return mergeMetrics(metrics);
}

function sortByStartDate(startDate1, startDate2) {
  return moment(startDate2).diff(moment(startDate1), 'seconds');
  //return dateFns.differenceInSeconds(startDate2, startDate1);
}

const formatReportWithSpendInActions = report => {
  const fromReport = R.path(R.__, report);
  const actions = (report.actions || []).map(action => ({
    // ...R.omit(['value'], action),
    ...action,
    conversions: action.value,
    spend: fromReport(['metric', 'spend']),
    clicks: fromReport(['metric', 'clicks']),
    spendOnActions: fromReport(['metric', 'spend']),
    clicksOnActions: fromReport(['metric', 'clicks']),
  }));
  return { ...report, actions };
};

const getTrackerConversions = actions => {
  const trackerEventNameMap = {
    pixel: 'pixel_event',
    ga: 'ga_event',
  };
  const data = R.pipe(R.groupBy(R.__('type')), R.pick(['pixel_event', 'ga_event']))(actions);
  let resultFormat = {};
  for (const [type, eventName] of Object.entries(trackerEventNameMap)) {
    resultFormat[type] = data[eventName] || [];
  }
  return resultFormat;
};

function aggregateByGroupKey(data, groupKeys = []) {
  if (!data || !data.length) return [];
  const numericKeys = Object.keys(data[0]).filter(key => !groupKeys.includes(key));
  const sumData = data.reduce((acc, curr) => {
    const groupKey = groupKeys.map(keyName => curr[keyName]).join('/');
    if (!acc[groupKey]) {
      acc[groupKey] = {};
    }
    numericKeys.forEach(propName => {
      if (!acc[groupKey][propName]) acc[groupKey][propName] = 0;
      acc[groupKey][propName] += curr[propName];
    });
    return acc;
  }, {});
  const parsedResult = Object.entries(sumData).reduce((acc, [groupKey, data]) => {
    const keys = groupKey.split('/');
    const result = {
      ...data,
    };
    groupKeys.forEach((key, index) => {
      result[key] = keys[index];
    });
    acc.push(result);
    return acc;
  }, []);
  return parsedResult;
}

function attachClickAndSpendToConversions(conversions = {}, { click = 0, spend = 0 } = {}) {
  return Object.entries(conversions).reduce((root, [trackerName, actionValues]) => {
    root[trackerName] = actionValues.map(actions => ({
      ...actions,
      clicksOnActions: click,
      spendOnActions: spend,
      conversions: actions.value,
    }));
    return root;
  }, {});
}

function extractSubChannelData({ withConversion = false } = {}) {
  return R.compose(
    R.map(([subChannel, data]) => {
      const mergedReportData = mergeReportArray(data);
      const result = {
        subChannel,
        ...mergedReportData,
      };
      if (withConversion) {
        const metric = mergedReportData.metric;
        const { spend, clicks: click } = metric;
        const conversions = getTrackerConversions(mergedReportData.actions);
        result.conversions = attachClickAndSpendToConversions(conversions, { click, spend });
      }
      return result;
    }),
    Object.entries,
    groupBySubChannel,
    R.flatten,
  );
}

function getEmptyConversionsByTracker() {
  return TRACKER_LIST.reduce((acc, tracker) => {
    acc[tracker] = {};
  }, {});
}

//TODO: remove
const filterConversionActions = function (actions) {
  if (!actions) {
    return [];
  }
  const _set = new Set(['pixel_event', 'ga_event', 'lead', 'mobile_event']);

  return actions.filter(({ type }) => _set.has(type));
  // return actions.filter(action=>action=="pixel_event"||action=="ga_event"||action=="lead"||action=="mobile_event")
};

const TRACKER_EVENT_MAPPING = {
  pixel_event: 'pixel',
  ga_event: 'ga',
  lead: 'lead',
  mobile_event: 'mobile',
  google_reports: 'google_reports',
  naver_reports: 'naver_reports',
  kakao_pixel: 'kakao_pixel',
  twitter_reports: 'twitter_reports',
  tiktok_reports: 'tiktok_reports',
  microsoft_reports: 'microsoft_reports',
  linkedin_reports: 'linkedin_reports',
  amazon_reports: 'amazon_reports',
  snapchat_reports: 'snapchat_reports',
  criteo_reports: 'criteo_reports',
  navergfa_reports: 'navergfa_reports',
  facebook: 'facebook'
};
function getConversionsByTracker(actions) {
  // const conversionActions = filterConversionActions(actions);

  // const conversionsGroupedByTracker = R.groupBy(
  //     ({ type }) => TRACKER_EVENT_MAPPING[type],
  //     conversionActions
  // );
  // return conversionsGroupedByTracker;
  if (!actions) return [];
  let conversionsGroupedByTracker = {};
  for (let action of actions) {
    let type = action.type;
    if (
      type == 'pixel_event' ||
      type == 'ga_event' ||
      type == 'lead' ||
      type == 'mobile_event' ||
      type == 'google_reports' ||
      type == 'naver_reports' ||
      type == 'kakao_pixel' ||
      type == 'twitter_reports' ||
      type == 'tiktok_reports' ||
      type == 'microsoft_reports' ||
      type == 'amazon_reports' ||
      type == 'snapchat_reports' ||
      type == 'linkedin_reports' ||
      type == 'criteo_reports' ||
      type == 'navergfa_reports'
    ) {
      let type2 = TRACKER_EVENT_MAPPING[action.type];
      if (!conversionsGroupedByTracker[type2]) conversionsGroupedByTracker[type2] = [];
      conversionsGroupedByTracker[type2].push(action);
    }
  }
  return conversionsGroupedByTracker;
}

function unionNameToKeyMap(nameToKeyMap) {
  const nameKeySet = nameToKeyMap.reduce((set, curr) => {
    Object.entries(curr).forEach(([name, key]) => {
      return set.add(`${name}:${key}`);
    });
    return set;
  }, new Set());

  const newMap = {};
  for (let nameToKey of nameKeySet.values()) {
    const [name, key] = nameToKey.split(':');
    if (name && key !== 'undefined' && key !== 'null') {
      newMap[name] = key;
    }
  }
  return newMap;
}

function mergeTwoPlacements(p1, p2) {
  if (R.isEmpty(p1)) return p2;
  if (R.isEmpty(p2)) return p1;
  const keys = uniq((Object.keys(p1) || []).concat(Object.keys(p2) || []));
  return keys.reduce((acc, key) => {
    acc[key] = uniq((p1[key] || []).concat(p2[key] || []));
    return acc;
  }, {});
}

function mergePlacements(placements) {
  return placements.reduce((acc, placement) => {
    return mergeTwoPlacements(acc, placement);
  }, {});
}

function getBudgetFromAdrielCampaign(adrielCampaign) {
  const proposalBudget = R.path(['proposal', 'budget'], adrielCampaign);
  const isLifeTimeBudget = proposalBudget.channels.some(channel => channel.isLifeTimeBudget);
  const budgetType = isLifeTimeBudget ? 'lifeTime' : 'daily';
  return {
    type: budgetType,
    value: proposalBudget[budgetType],
  };
}

function generateId({ channel, level, channelId }) {
  if (!level) return `${channel}:${channelId}`;
  return `${channel}:${level}:${channelId}`;
}

function getInfoFromId(id) {
  if (typeof id === 'number') return { channelId: id };
  const splittedId = id.split(':');
  let channel, level, channelId;
  const nbOfInfo = splittedId.length;
  if (nbOfInfo === 3) {
    channel = splittedId[0];
    level = splittedId[1];
    channelId = splittedId[2];
  } else if (nbOfInfo === 2) {
    channel = splittedId[0];
    channelId = splittedId[1];
  } else {
    channelId = id;
  }
  return {
    channel,
    level,
    channelId,
  };
}

function getParentLevel(level) {
  switch (level) {
    case 'campaign':
      return 'adAccount';
    case 'adSet':
      return 'campaign';
    case 'ad':
      return 'adSet';
    default:
      return null;
  }
}

function getLevelChannelId(element, level) {
  switch (level) {
    case 'adAccount':
      return element.channelId;
    case 'campaign':
      return element.channelCampaignId;
    case 'adSet':
      return element.channelAdSetId;
    case 'ad':
      return element.channelAdId;
    case 'keyword':
      return element.channelKeywordId;
    default:
      return null;
  }
}

function getLevelAndParentIds({ element, channel, level, adAccountId }) {
  const parentIds = [];
  if (['campaign', 'adSet', 'ad', 'keyword'].includes(level)) {
    const parentLevel = 'adAccount';
    parentIds.push({
      level: 'adAccount',
      parentId: generateId({
        channel,
        level: parentLevel,
        channelId: adAccountId,
      }),
    });
  }
  if (['adSet', 'ad', 'keyword'].includes(level)) {
    const parentLevel = 'campaign';
    const channelId = getLevelChannelId(element, 'campaign');
    parentIds.push({
      level: parentLevel,
      parentId: generateId({ channel, level: parentLevel, channelId }),
    });
  }
  if (['ad', 'keyword'].includes(level)) {
    const parentLevel = 'adSet';
    const channelId = getLevelChannelId(element, 'adSet');
    parentIds.push({
      level: parentLevel,
      parentId: generateId({ channel, level: parentLevel, channelId }),
    });
  }
  return parentIds;
}
/**
 * @param  {{ startDate, endDate, continuously }[]} schedules
 * @return {{ startDate: String, endDate: String }}
 */
function mergeSchedules(schedules) {
  schedules = schedules.filter(Boolean);
  const startDates = schedules.map(s => s.startDate).filter(Boolean);
  const minStartDate = moment.min(startDates.map(date => moment(date))).format('YYYY-MM-DD');
  let endDate;
  // if no continuously schedule, check the max endDate
  if (schedules.every(schedule => !schedule.continuously)) {
    const endDates = schedules.map(s => s.endDate).filter(Boolean);
    if (endDates.length > 0) {
      endDate = moment.max(endDates.map(date => moment(date))).format('YYYY-MM-DD');
    }
  }
  if (!endDate && schedules.some(s => s.deactivatedAt)) {
    const deactivatedAtDates = schedules.map(s => s.deactivatedAt);
    endDate = moment.max(deactivatedAtDates.map(date => moment(date))).format('YYYY-MM-DD');
  }
  return {
    startDate: minStartDate,
    endDate,
  };
}

/**
 * Set the first letter to upper-case.
 * @param {string} s
 * @returns {string}
 */
function capitalize(s) {
  return s[0].toUpperCase() + s.slice(1);
}

const _defaultString = R.ifElse(R.identity, R.identity, R.always(''));

const upperHead = R.compose(R.converge(R.concat, [R.compose(R.toUpper, R.head), R.tail]), _defaultString);

const upperWords = R.compose(R.join(' '), R.map(upperHead), R.split(' '), R.trim(), _defaultString);

function getParentLevels(level) {
  if (level == 'adAccount') {
    return [];
  }
  if (level == 'campaign') {
    return ['adAccount'];
  }
  if (level == 'adSet') {
    return ['adAccount', 'campaign'];
  }
  if (level == 'ad') {
    return ['adAccount', 'campaign', 'adSet'];
  }
  if (level == 'keyword') {
    return ['adAccount', 'campaign', 'adSet'];
  }
  return [];
}

module.exports = {
  sumObject,
  groupBySubChannel,
  getListMinTime,
  getListMaxTime,
  mergeActions,
  mergeMetrics,
  mergeConversionsByTracker,
  mergeConversionsByTrackerPerDate,
  mergeSubChannelConversions,
  mergeSubChannelData,
  getMetricFromList,
  getTime,
  transformGAMetric,
  getAdrielStatus,
  mergeReportArray,
  mergeReports,
  sortByStartDate,
  mergeReportArrayByDate,
  formatReportWithSpendInActions,
  getTrackerConversions,
  aggregateByGroupKey,
  attachClickAndSpendToConversions,
  extractSubChannelData,
  getEmptyConversionsByTracker,
  filterConversionActions,
  getConversionsByTracker,
  unionNameToKeyMap,
  mergePlacements,
  getBudgetFromAdrielCampaign,
  generateId,
  getInfoFromId,
  getParentLevel,
  getParentLevels,
  getLevelChannelId,
  getLevelAndParentIds,
  mergeSchedules,
  capitalize,
  upperWords,
  TRACKER_EVENT_MAPPING,
};
